import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  RotateCcw, 
  BookOpen,
  Clock,
  CheckCircle
} from 'lucide-react';

const automationSteps = [
  {
    icon: Mail,
    title: 'Email Follow-ups',
    description: 'Automated email sequences that nurture leads based on their behavior and interests.',
    timing: 'Day 1, 3, 7, 14',
    status: 'active'
  },
  {
    icon: RotateCcw,
    title: 'Retargeting Ads',
    description: 'Strategic ad placements that keep your brand top-of-mind for engaged visitors.',
    timing: 'Continuous',
    status: 'active'
  },
  {
    icon: BookOpen,
    title: 'Content Nurturing',
    description: 'Educational content delivered at the right time to move prospects toward purchase.',
    timing: 'Based on engagement',
    status: 'active'
  },
];

const timelineItems = [
  { day: 'Day 1', action: 'Welcome Email', type: 'email' },
  { day: 'Day 2', action: 'Retargeting Active', type: 'ad' },
  { day: 'Day 3', action: 'Value Content', type: 'email' },
  { day: 'Day 5', action: 'Case Study', type: 'email' },
  { day: 'Day 7', action: 'Offer Email', type: 'email' },
  { day: 'Day 14', action: 'Follow-up', type: 'email' },
];

export default function AutomationSection() {
  return (
    <section 
      id="automation"
      data-testid="automation-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#080510] to-[#030303]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-4 block">
            The Nurturing Layer
          </span>
          <h2 
            data-testid="automation-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Marketing
            <br />
            <span className="text-purple-400">Automation</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            Systems that work around the clock, nurturing leads while you 
            focus on what matters most.
          </p>
        </motion.div>
        
        {/* Automation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {automationSteps.map((step, index) => (
            <motion.div
              key={index}
              data-testid={`automation-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="group"
            >
              <div className="glass rounded-2xl p-6 h-full card-hover border border-purple-500/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                    <step.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    Active
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {step.description}
                </p>
                
                <div className="flex items-center gap-2 text-xs text-purple-400/70">
                  <Clock className="w-3 h-3" />
                  <span>{step.timing}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Timeline Animation */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-center text-sm text-gray-500 uppercase tracking-wider mb-8">
            Automated Sequence Timeline
          </h3>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute top-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
            
            {/* Timeline items */}
            <div className="flex justify-between">
              {timelineItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col items-center"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      boxShadow: [
                        '0 0 0 rgba(168, 85, 247, 0)',
                        '0 0 20px rgba(168, 85, 247, 0.4)',
                        '0 0 0 rgba(168, 85, 247, 0)'
                      ]
                    }}
                    transition={{ 
                      duration: 2,
                      delay: index * 0.3,
                      repeat: Infinity
                    }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                      item.type === 'email' ? 'bg-purple-500/20' : 'bg-cyan-500/20'
                    }`}
                  >
                    {item.type === 'email' ? (
                      <Mail className="w-5 h-5 text-purple-400" />
                    ) : (
                      <RotateCcw className="w-5 h-5 text-cyan-400" />
                    )}
                  </motion.div>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    {item.day}
                  </span>
                  <span className="text-xs text-gray-400 text-center max-w-[80px] hidden sm:block">
                    {item.action}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Result */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="flex justify-center mt-12"
          >
            <div className="inline-flex items-center gap-3 glass-heavy rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Leads nurtured automatically 24/7</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
